import React, { useEffect, useState } from "react";
import { Button, Form, Header, Input, Message, Segment } from "semantic-ui-react";
import { toast } from "react-toastify";
import { ToasterConfig } from "../../../config/ToasterConfig";
import useOperationTypeService from "../../../services/backoffice/hooks/useOperationTypeService";
import { UpdateOperationTypeRequest } from "../../../models/requests/create-operation-type-request";

interface OperationTypeFormProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
    operationTypeId?: string;
}

const OperationTypeForm: React.FC<OperationTypeFormProps> = ({ operationTypeId, onClose }) => {
    const [type, setType] = useState<Partial<UpdateOperationTypeRequest>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const { createOperationType, updateOperationType, getOperationTypeById } = useOperationTypeService();

    useEffect(() => {
        if (operationTypeId) {
            setLoading(true);
            getOperationTypeById(operationTypeId)
                .then((response) => {
                    setType(response);
                })
                .catch(() => {
                    setError("Erro ao carregar tipo de operação.");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [operationTypeId]);

    const handleSubmit = async () => {
        try {
            if (!type.code || !type.name) {
                setError("Todos os campos são obrigatórios.");
                return;
            }
            
            setLoading(true);
            if (operationTypeId) {
                await updateOperationType(operationTypeId, { name: type.name, code: type.code });
                toast.success("Tipo de operação atualizado com sucesso", ToasterConfig.success);
            } else {
                await createOperationType({ name: type.name, code: type.code });
                toast.success("Tipo de operação criado com sucesso", ToasterConfig.success);
            }
            if (onClose) onClose();
        } catch (error) {
            setError("Erro ao salvar tipo de operação.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setType((prevType) => ({
            ...prevType,
            [name]: value,
        }));
    };

    const handleClear = () => {
        setType({});
    };

    const isSubmitDisabled = !type.code || !type.name;

    return (
        <Segment raised style={{ maxWidth: "600px", margin: "5px auto", padding: "20px" }}>
            <Header
                as="h3"
                style={{
                    background: "#49AB3AFF",
                    color: "#fff",
                    display: "flex",
                    alignItems: "left",
                    justifyContent: "flex-start",
                    padding: "10px",
                    margin: "0",
                    borderRadius: "5px 5px 0 0",
                }}
            >
                {operationTypeId ? "Editar Tipo de Operação" : "Cadastro de Tipos de Operação"}
            </Header>
            <Form onSubmit={handleSubmit}>
                <div style={{
                    border: "2px solid #d4d4d5",
                    borderRadius: "4px",
                    padding: "10px",
                    margin: "10px 0"
                }}>
                    <Form.Group widths="equal">
                        <Form.Field required>
                            <label>Código</label>
                            <Input
                                name="code"
                                placeholder="Digite o código"
                                onChange={handleInputChange}
                                value={type.code || ""}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !type.code}
                            />
                        </Form.Field>
                        <Form.Field required>
                            <label>Tipo de Operação</label>
                            <Input
                                name="name"
                                placeholder="Digite o tipo"
                                onChange={handleInputChange}
                                value={type.name || ""}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !type.name}
                            />
                        </Form.Field>
                    </Form.Group>
                </div>
                <div style={{ textAlign: "right" }}>
                    <Button color="green" type="submit" disabled={isSubmitDisabled || loading}>
                        {operationTypeId ? "Atualizar" : "Salvar"}
                    </Button>
                    <Button type="button" onClick={handleClear} disabled={loading}>
                        Limpar
                    </Button>
                    <Button type="button" onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                </div>
                {error && <Message negative>{error}</Message>}
            </Form>
        </Segment>
    );
};

export default OperationTypeForm;
